import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { cleanString } from "../../../utils";
import { LoadedSince } from "../../../shared-components/LoadedSince";
import Pagination from "../../../shared-components/Pagination";

import { genericErrorLoadingMessage } from "../../utils";

import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import { useAppContext } from "../../../AppProviders";
import { CacheKeys } from "../../../api/Cache";
import { LazyLoadTable } from "../../../shared-components/LazyLoadTable";
import InternalIqaMisconductLazyRow, {
  InternalIqaMisconductV2026RowProvider,
  type InternalIqaMisconductV2026RowContextValue,
} from "../InternalIqaMisconductLazyRow";
import { useMisconductReportSummaryListQuery } from "../misconductListQueries";
import useNavigateWithState from "../../useNavigateWithState";

const pageSize = 15;

/**
 * Internal QA tab — ``GET /lists/misconduct/internal_qa``.
 * Per-row step via ``GET /workflow_runs/{uuid}/latest_step_run`` (FAILED rows skip the fetch).
 * Legacy: ``../../InternalQATab.tsx``.
 */
export function InternalQATabV2026() {
  const {
    canCancelMisconduct,
    canViewAllReportsWithinCompany,
    canInternalQaEdit,
  } = usePermissionValidator();
  const { refreshCache } = useAppContext();
  const { navigateWithState } = useNavigateWithState();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [filter, setFilter] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const v2026IqaQuery = useMisconductReportSummaryListQuery({
    tab: "internal_qa",
    individual: !canViewAllReportsWithinCompany,
    names: filter,
    page,
    pageSize,
    enabled: true,
  });
  const SearchName = searchParams.get("search");
  const SearchPage = searchParams.get("page");

  const rowContextValue = useMemo<InternalIqaMisconductV2026RowContextValue>(
    () => ({
      canInternalQaEdit,
      canCancelMisconduct,
      navigateWithState,
    }),
    [canInternalQaEdit, canCancelMisconduct, navigateWithState]
  );

  function buildHeader() {
    const tableHeader = [
      "Name / Company",
      "Status",
      "Previously QA?",
      "Check Type / Behavior Kit",
      "Date Added",
      "Date Due",
      "Report ID",
      "Client Reference ID",
    ];
    if (canCancelMisconduct) {
      tableHeader.push("Cancel Report");
    }
    return tableHeader;
  }

  useEffect(() => {
    setFilter(cleanString(SearchName?.split(" ").join("%20") ?? ""));
    setPage(Number(1));
  }, [SearchName]);

  useEffect(() => {
    setPage(Number(SearchPage || "1"));
  }, [SearchPage]);

  if (v2026IqaQuery.error) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  const v2026Data = v2026IqaQuery.data;
  return (
    <>
      <LoadedSince
        reportData={v2026Data}
        onClear={() => {
          refreshCache(CacheKeys.reports);
          void v2026IqaQuery.refetch();
        }}
      />
      <InternalIqaMisconductV2026RowProvider value={rowContextValue}>
        <LazyLoadTable
          parentRows={v2026Data?.report_summaries}
          parentLoading={v2026IqaQuery.isLoading}
          headers={buildHeader()}
          emptyMessage="No search Items"
          keyField="report_uuid"
          rowFunction={InternalIqaMisconductLazyRow}
        />
      </InternalIqaMisconductV2026RowProvider>
      {!v2026IqaQuery.isLoading && v2026Data && (
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={v2026Data.total_rows}
          search={filter}
        />
      )}
    </>
  );
}
